<template>
  <div v-if="modelValue" class="modal-overlay" @click.self="close">
    <div class="modal-card animate-scale-in" role="dialog" aria-labelledby="modal-title">
      <div class="modal-header">
        <div class="header-text">
          <span class="modal-kicker">{{ form.id ? '評価の編集・更新' : 'AI好み学習 / 履歴追加' }}</span>
          <h2 id="modal-title" class="modal-title">
            {{ form.id ? '旅行評価を編集・更新' : '過去の旅行評価を追加・学習' }}
          </h2>
        </div>
        <button type="button" class="btn-close" @click="close" aria-label="閉じる">×</button>
      </div>

      <form @submit.prevent="handleSubmit" class="modal-body">
        <p class="modal-desc">
          {{ form.id
            ? '旅行の評価スコアや感想メモを更新します。更新内容は集計や次の旅行先提案に即座に反映されます。'
            : 'しおりを作っていない過去の旅行の思い出や評価を記録すると、好みの分析やおすすめ提案の精度がさらに向上します。'
          }}
        </p>

        <!-- 旅行名・行き先 -->
        <div class="form-group">
          <label class="form-label" for="trip-title">
            旅行名・行き先 <span class="required">*</span>
          </label>
          <input
            id="trip-title"
            v-model="form.title"
            type="text"
            class="form-input"
            placeholder="例: 北海道・札幌グルメ旅、2023年 沖縄リゾート"
            required
          />
        </div>

        <!-- 旅行時期 & 参加者 -->
        <div class="form-row">
          <div class="form-group">
            <label class="form-label" for="trip-date">旅行時期（任意）</label>
            <input
              id="trip-date"
              v-model="form.date"
              type="text"
              class="form-input"
              placeholder="例: 2024年10月、2年前の夏"
            />
          </div>
          <div class="form-group">
            <label class="form-label" for="trip-members">同行者（任意）</label>
            <input
              id="trip-members"
              v-model="form.members"
              type="text"
              class="form-input"
              placeholder="例: 家族、友人（カンマ区切り）"
            />
          </div>
        </div>

        <!-- 総合評価 -->
        <div class="form-group rating-group-main">
          <label class="form-label">総合満足度 <span class="required">*</span></label>
          <div class="star-rating-picker">
            <button
              v-for="star in 5"
              :key="star"
              type="button"
              class="star-btn"
              :class="{ active: form.overallScore >= star }"
              @click="form.overallScore = star"
            >
              ★
            </button>
            <span class="rating-value-label">{{ form.overallScore }}.0 / 5.0</span>
          </div>
        </div>

        <!-- カテゴリー別スコア -->
        <div class="category-ratings-box">
          <h4 class="category-box-title">カテゴリー別評価</h4>
          <div class="category-rating-row" v-for="cat in categories" :key="cat.key">
            <span class="cat-label">{{ cat.icon }} {{ cat.label }}</span>
            <div class="star-picker-sm">
              <button
                v-for="s in 5"
                :key="s"
                type="button"
                class="star-sm-btn"
                :class="{ active: form[cat.key + 'Score'] >= s }"
                @click="form[cat.key + 'Score'] = s"
              >
                ★
              </button>
              <span class="cat-score-num">{{ form[cat.key + 'Score'] }}.0</span>
            </div>
          </div>
        </div>

        <!-- メモ・感想 -->
        <div class="form-group">
          <label class="form-label" for="trip-memo">メモ・感想（任意）</label>
          <textarea
            id="trip-memo"
            v-model="form.memo"
            class="form-textarea"
            rows="2"
            placeholder="例: 海鮮がとても美味しかった。ホテルの温泉が最高だった。"
          ></textarea>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="close">キャンセル</button>
          <button type="submit" class="btn btn-primary">
            <span>{{ form.id ? '旅行評価を更新する' : '旅行評価を学習させる' }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { reactive, watch } from 'vue';

/* global defineProps, defineEmits */
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  initialData: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue', 'save']);

const categories = [
  { key: 'lodging', label: '宿泊・ホテル', icon: '🏨' },
  { key: 'food', label: '食事・グルメ', icon: '🍽️' },
  { key: 'sightseeing', label: '観光・名所', icon: '⛩️' },
  { key: 'activity', label: 'アクティビティ', icon: '🏄' },
  { key: 'transport', label: '交通・移動', icon: '🚆' },
];

const form = reactive({
  id: '',
  title: '',
  date: '',
  members: '',
  overallScore: 5,
  lodgingScore: 4,
  foodScore: 5,
  sightseeingScore: 4,
  activityScore: 4,
  transportScore: 4,
  memo: '',
  shioriId: null,
  isManual: true,
});

watch(
  () => props.initialData,
  (val) => {
    if (val) {
      form.id = val.id || '';
      form.title = val.title || val.SUBJECT_NMV || '';
      form.date = val.date || '';
      form.members = Array.isArray(val.members) ? val.members.join(', ') : (val.members || '');
      form.overallScore = val.overallScore ?? 5;
      form.lodgingScore = val.lodgingScore ?? 4;
      form.foodScore = val.foodScore ?? 5;
      form.sightseeingScore = val.sightseeingScore ?? 4;
      form.activityScore = val.activityScore ?? 4;
      form.transportScore = val.transportScore ?? 4;
      form.memo = val.memo || '';
      form.shioriId = val.shioriId || val.SHIORI_NOV || null;
      form.isManual = val.isManual !== false;
    } else {
      form.id = '';
      form.title = '';
      form.date = '';
      form.members = '';
      form.overallScore = 5;
      form.lodgingScore = 4;
      form.foodScore = 5;
      form.sightseeingScore = 4;
      form.activityScore = 4;
      form.transportScore = 4;
      form.memo = '';
      form.shioriId = null;
      form.isManual = true;
    }
  },
  { immediate: true }
);

const close = () => {
  emit('update:modelValue', false);
};

const handleSubmit = () => {
  if (!form.title.trim()) return;

  const membersArray = typeof form.members === 'string' && form.members
    ? form.members.split(/[,、]/).map((m) => m.trim()).filter(Boolean)
    : (Array.isArray(form.members) ? form.members : []);

  const payload = {
    id: form.id || ('manual_' + Date.now()),
    title: form.title.trim(),
    date: form.date ? String(form.date).trim() : '',
    members: membersArray,
    overallScore: Number(form.overallScore),
    lodgingScore: Number(form.lodgingScore),
    foodScore: Number(form.foodScore),
    sightseeingScore: Number(form.sightseeingScore),
    activityScore: Number(form.activityScore),
    transportScore: Number(form.transportScore),
    memo: form.memo.trim(),
    createdAt: new Date().toISOString(),
    isManual: form.isManual,
    shioriId: form.shioriId,
  };

  emit('save', payload);
  close();
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(18, 33, 52, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal-card {
  background: var(--color-surface);
  border-radius: var(--radius-xl);
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-xl);
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.animate-scale-in {
  animation: scaleIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.modal-header {
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid var(--color-border-light);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.modal-kicker {
  color: var(--color-primary-ink);
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: block;
}

.modal-title {
  font-size: 1.1875rem;
  font-weight: 800;
  color: var(--color-text);
  margin: 0.15rem 0 0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  color: var(--color-text-muted);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: var(--radius-sm);
  transition: all var(--transition-fast);
}

.btn-close:hover {
  background: var(--color-background);
  color: var(--color-text);
}

.modal-body {
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.modal-desc {
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  line-height: 1.5;
  margin: 0;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.form-label {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--color-text);
}

.required {
  color: var(--color-danger);
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 0.6rem 0.85rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-background);
  color: var(--color-text);
  font-size: 0.875rem;
  font-family: inherit;
  transition: all var(--transition-fast);
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: var(--color-primary-ink);
  background: var(--color-surface);
  box-shadow: 0 0 0 2px var(--color-primary-light);
}

/* 総合評価スター */
.rating-group-main {
  background: var(--color-background);
  padding: 0.875rem 1rem;
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border);
}

.star-rating-picker {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.25rem;
}

.star-btn {
  background: none;
  border: none;
  font-size: 1.6rem;
  color: #d1d5db;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: all var(--transition-fast);
}

.star-btn.active {
  color: var(--color-review);
}

.rating-value-label {
  margin-left: 0.5rem;
  font-size: 0.9375rem;
  font-weight: 800;
  color: var(--color-text);
}

/* カテゴリー別評価 */
.category-ratings-box {
  background: var(--color-background);
  padding: 0.875rem 1rem;
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.category-box-title {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0;
}

.category-rating-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.cat-label {
  font-size: 0.8125rem;
  color: var(--color-text);
  font-weight: 600;
}

.star-picker-sm {
  display: flex;
  align-items: center;
  gap: 0.2rem;
}

.star-sm-btn {
  background: none;
  border: none;
  font-size: 1.15rem;
  color: #d1d5db;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: all var(--transition-fast);
}

.star-sm-btn.active {
  color: var(--color-review);
}

.cat-score-num {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--color-text-muted);
  width: 2rem;
  text-align: right;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--color-border-light);
}

@media (max-width: 600px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
